// V3 — costing each shore-split cell against the `costPerRole` table.
// Hub-aware: the per-discipline OGS config picks a single hub; its shore
// leg is priced with that hub's costPerRole row. Onshore stays as a single
// shore with no hub breakdown (per spec).

import type { ClientProfile, LevelId, Model } from '../types.js';
import { lookupCostPerRole } from './table-lookup.js';
import type { ShoreSplit } from './shore-split.js';
import { resolveOgsDisciplineConfig } from './shore-split.js';

export interface CellCost {
  onshore: number;
  nearshore: number;
  offshore: number;
  total: number;
}

export function costForCell(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  channelId: string,
  levelId: LevelId,
  split: ShoreSplit,
): CellCost {
  // Mirror shore-split's hub resolution so cost stays in sync with the
  // hub the FTE was allocated against: the OGS FTE lands at the single hub
  // picked in the matching (discipline, channel) config, so both legs price
  // against that hub.
  const hubId = resolveOgsDisciplineConfig(profile, disciplineId, channelId)?.hubId;

  const onCost = lookupCostPerRole(model, 'onshore', disciplineId, channelId, levelId);
  const nsCost = lookupCostPerRole(
    model,
    'nearshore',
    disciplineId,
    channelId,
    levelId,
    hubId,
  );
  const offCost = lookupCostPerRole(
    model,
    'offshore',
    disciplineId,
    channelId,
    levelId,
    hubId,
  );
  const onshore = split.onshore * onCost;
  const nearshore = split.nearshore * nsCost;
  const offshore = split.offshore * offCost;
  return { onshore, nearshore, offshore, total: onshore + nearshore + offshore };
}
